import { Link } from "react-router-dom";
import {
  CheckCircle2, Truck, Package, MapPin, Navigation, CreditCard,
  Clock, Sparkles, AlertTriangle, Loader2,
} from "lucide-react";
import { formatAddress } from "@/lib/formatAddress";

/**
 * Phase 43.51 — Blocco "PROSSIMA AZIONE" lato cliente.
 *
 * Mostra in alto, sopra "Identificazione pacco", lo stato live dell'ordine
 * con prossimo step, eventuale ETA, e CTA contestuale (paga / tracking / valuta).
 *
 * Props:
 *  - order: oggetto ordine (richiesto)
 *  - tracking: payload di /tracking se disponibile (driver + ETA)
 *  - onPay?: callback opzionale per riavviare il checkout
 *  - shareTrackingUrl?: link tracking pubblico (per CTA "Condividi tracking")
 */
export default function CustomerLiveActionCard({ order, tracking, onPay, shareTrackingUrl }) {
  if (!order) return null;

  const status = order.status;
  const paymentStatus = order.payment_status;
  const cancelled = status === "cancelled" || status === "refunded";

  // Mappa stato → blocco live
  const block = resolveBlock({ status, paymentStatus, tracking, order });
  if (!block) return null;

  const Icon = block.icon;

  return (
    <section
      className="card-soft p-5 sm:p-6 border-l-4 overflow-hidden"
      style={{ borderLeftColor: block.accent }}
      data-testid="customer-live-action-card"
    >
      <div className="flex items-start gap-4">
        <span
          className="inline-flex h-12 w-12 items-center justify-center rounded-2xl shrink-0 shadow-sm"
          style={{ background: block.accent, color: "white" }}
        >
          <Icon className="h-6 w-6" />
        </span>

        <div className="flex-1 min-w-0">
          <p className="overline" style={{ color: block.accent }} data-testid="live-action-label">
            {cancelled ? "Stato ordine" : "Stato attuale"}
          </p>
          <h2
            className="font-display text-lg sm:text-xl font-bold tracking-tight mt-0.5"
            data-testid="live-action-title"
          >
            {block.title}
          </h2>
          {block.subtitle && (
            <p className="mt-1 text-sm text-dapnow-muted" data-testid="live-action-subtitle">
              {block.subtitle}
            </p>
          )}

          {block.etaMinutes != null && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-white/80 border border-dapnow-line px-2.5 py-1" data-testid="live-action-eta">
              <Clock className="h-3.5 w-3.5 text-dapnow-blue" />
              ETA ~ {block.etaMinutes} min
            </p>
          )}

          {/* Phase 43.59 — Badge "coperta dal Credito Consegne" */}
          {order.payment_source === "delivery_pack" && !cancelled && (
            <p
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 px-2.5 py-1"
              data-testid="live-action-covered-by-pack"
            >
              ✦ Coperta dal tuo Credito Consegne
            </p>
          )}

          {block.cta && (
            <div className="mt-4 flex flex-wrap gap-2">
              {block.cta.primary && (
                block.cta.primary.onClick ? (
                  <button
                    type="button"
                    onClick={block.cta.primary.onClick}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-dapnow-ink text-white px-4 h-10 text-sm font-bold hover:bg-dapnow-blue transition"
                    data-testid="live-action-cta-primary"
                  >
                    {block.cta.primary.icon ? <block.cta.primary.icon className="h-4 w-4" /> : null}
                    {block.cta.primary.label}
                  </button>
                ) : (
                  <Link
                    to={block.cta.primary.to}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-dapnow-ink text-white px-4 h-10 text-sm font-bold hover:bg-dapnow-blue transition"
                    data-testid="live-action-cta-primary"
                  >
                    {block.cta.primary.icon ? <block.cta.primary.icon className="h-4 w-4" /> : null}
                    {block.cta.primary.label}
                  </Link>
                )
              )}
              {block.cta.secondary && (
                <a
                  href={block.cta.secondary.href}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-dapnow-line bg-white px-3 h-10 text-sm font-semibold text-dapnow-ink hover:border-dapnow-blue/40"
                  data-testid="live-action-cta-secondary"
                  {...(block.cta.secondary.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  {block.cta.secondary.icon ? <block.cta.secondary.icon className="h-4 w-4" /> : null}
                  {block.cta.secondary.label}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );

  /* eslint-disable no-shadow */
  function resolveBlock({ status, paymentStatus, tracking, order }) {
    // 1) Pagamento ancora pending → la prossima azione è pagare
    if (paymentStatus === "pending" && status !== "cancelled") {
      return {
        icon: CreditCard,
        accent: "#D97706",
        title: "Completa il pagamento per attivare l'ordine",
        subtitle: "Una volta pagato, cerchiamo un driver compatibile sulla tratta.",
        cta: onPay ? { primary: { label: "Paga ora", onClick: onPay, icon: CreditCard } } : null,
      };
    }
    if (cancelled) {
      return {
        icon: AlertTriangle,
        accent: "#B91C1C",
        title: status === "refunded" ? "Ordine rimborsato" : "Ordine annullato",
        subtitle: order.cancel_reason || "Contatta il supporto per maggiori dettagli.",
      };
    }
    const eta = tracking?.eta_minutes ?? null;
    const driverName = tracking?.driver?.name || order.driver_name;
    switch (status) {
      case "pending":
        return {
          icon: Loader2,
          accent: "#0052FF",
          title: "Cerchiamo un driver compatibile",
          subtitle: "Appena un driver accetta, vedrai qui i dettagli e l'ETA.",
        };
      case "accepted":
        return {
          icon: Truck,
          accent: "#F59E0B",
          title: driverName ? `${driverName} è in arrivo al ritiro` : "Driver in arrivo al ritiro",
          subtitle: order.pickup_address ? `Ritiro: ${formatAddress(order.pickup_address)}` : null,
          etaMinutes: eta,
          cta: shareTrackingUrl ? {
            primary: { label: "Apri tracking live", to: "#tracking", icon: Navigation },
            secondary: { label: "Condividi", href: shareTrackingUrl, icon: Sparkles, external: true },
          } : null,
        };
      case "picking_up":
        return {
          icon: MapPin,
          accent: "#F59E0B",
          title: "Driver al punto di ritiro",
          subtitle: "Pacco in fase di ritiro. Assicurati che sia pronto con il #ID scritto sopra.",
          etaMinutes: eta,
        };
      case "picked_up":
        return {
          icon: Package,
          accent: "#0052FF",
          title: "Pacco ritirato",
          subtitle: "Il pacco è in viaggio verso la consegna.",
          etaMinutes: eta,
        };
      case "delivering":
        return {
          icon: Truck,
          accent: "#0052FF",
          title: driverName ? `${driverName} è in consegna` : "Driver in consegna",
          subtitle: order.delivery_address ? `Destinazione: ${formatAddress(order.delivery_address)}` : null,
          etaMinutes: eta,
          cta: shareTrackingUrl ? {
            primary: { label: "Tracking live", to: "#tracking", icon: Navigation },
            secondary: { label: "Condividi", href: shareTrackingUrl, icon: Sparkles, external: true },
          } : null,
        };
      case "delivered":
        return {
          icon: CheckCircle2,
          accent: "#16A34A",
          title: "Consegnato",
          subtitle: order.rating_value ? "Grazie per aver valutato il servizio." : null,
        };
      default:
        return null;
    }
  }
  /* eslint-enable no-shadow */
}
